import React from "react";
import { useLocation } from "react-router-dom";
import { ChevronDown, ChevronRight, Circle, LucideIcon } from "lucide-react";
import * as Icons from "lucide-react";
import { Link } from "react-router-dom";
import { Box, Button } from "@radix-ui/themes";
import { useAuth } from "@/providers/AuthProvider";

interface NavigationItemProps {
  id: string;
  label: string;
  icon: string;
  route: string;
  subItems: { label: string; route: string }[];
  isActive: boolean;
  isExpanded: boolean;
  isSidebarExpanded: boolean;
  onItemClick: (id: string) => void;
  onSubItemClick: (parentId: string, subItemLabel: string) => void;
}

export function NavigationItem({
  id,
  label,
  icon,
  route,
  subItems,
  isActive,
  isExpanded,
  isSidebarExpanded,
  onItemClick,
  onSubItemClick,
}: NavigationItemProps) {
  const { currentTenant } = useAuth();
  const pathname = useLocation().pathname;

  const isSubItemActive = (route: string) => pathname === route;

  // Dynamically get the icon component
  const Icon = (Icons as unknown as Record<string, LucideIcon>)[icon] || Circle;

  const safeRoute = currentTenant
    ? route.startsWith(`/org/${currentTenant.id}`)
      ? route
      : `/org/${currentTenant.id}${route}`
    : route;

  // Determine if this is a navigable route
  const isNavigable = route !== "#";

  return (
    <Box mb="1">
      <Link
        to={isNavigable ? safeRoute : "#"}
        className="nav-item-link"
        aria-label={isSidebarExpanded ? undefined : label}
        title={isSidebarExpanded ? undefined : label}
        onClick={(e) => {
          // Only prevent default for non-navigable links (# links)
          if (!isNavigable) {
            e.preventDefault();
          }
          onItemClick(id);
        }}
        style={{
          textDecoration: "none",
          display: "flex",
          alignItems: "center",
          padding: "8px 6px",
          fontSize: "14px",
          borderRadius: "var(--radius-3)",
          transition: "all 150ms ease",
          backgroundColor: isActive ? "var(--accent-a3)" : "transparent",
          color: "var(--gray-12)",
          cursor: "pointer",
        }}
        onMouseEnter={(e) => {
          if (!isActive) {
            e.currentTarget.style.backgroundColor = "var(--gray-a3)";
          }
        }}
        onMouseLeave={(e) => {
          if (!isActive) {
            e.currentTarget.style.backgroundColor = "transparent";
          }
        }}
      >
        <Icon
          style={{
            width: "20px",
            height: "20px",
            marginRight: isSidebarExpanded ? "12px" : "0",
          }}
        />
        {isSidebarExpanded && (
          <>
            <span style={{ flex: 1 }}>{label}</span>
            {subItems.length > 0 && (
              <Box ml="auto">
                {isExpanded ? (
                  <ChevronDown style={{ width: "16px", height: "16px" }} />
                ) : (
                  <ChevronRight style={{ width: "16px", height: "16px" }} />
                )}
              </Box>
            )}
          </>
        )}
      </Link>

      {isSidebarExpanded && isExpanded && subItems.length > 0 && (
        <Box
          mt="1"
        style={{
            display: "flex",
            flexDirection: "column",
            gap: "2px",
            paddingLeft: "38px", // Align exactly with parent text (6px padding + 20px icon + 12px margin)
            paddingRight: "6px", // Match parent's right padding
          }}
        >
          {subItems.map((subItem) => (
            <Button
              key={subItem.label}
              variant="ghost"
              size="1"
              className="nav-sub-item"
              onClick={() => onSubItemClick(id, subItem.label)}
              style={{
                width: "100%",
                justifyContent: "flex-start",
                padding: "4px 0 4px 8px", // Container provides right padding
                fontSize: "13px",
                fontWeight: "normal",
                minHeight: "24px",
                backgroundColor: isSubItemActive(subItem.route)
                  ? "var(--accent-a3)"
                  : "transparent",
                color: "var(--gray-11)",
                cursor: "pointer",
                borderRadius: "var(--radius-2)",
                margin: "1px 0",
                marginLeft: "-8px", // Offset to maintain text alignment
              }}
              onMouseEnter={(e) => {
                if (!isSubItemActive(subItem.route)) {
                  e.currentTarget.style.backgroundColor = "var(--gray-a3)";
                }
              }}
              onMouseLeave={(e) => {
                if (!isSubItemActive(subItem.route)) {
                  e.currentTarget.style.backgroundColor = "transparent";
                }
              }}
                          >
                {subItem.label}
              </Button>
          ))}
        </Box>
      )}
    </Box>
  );
}
